/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext } from 'react'

// @mui core components
import { Box, Card, Grid, Typography, Button } from '@mui/material'

// Settings page components
import FormField from 'components/FormField'

// Data
import { selectData } from 'layouts/onboarding'
import { IntakeContext } from 'context/IntakeContext'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function BasicInfo() {
  const { intakeState, updateIntakeStateAtKey, updateUser, currentErrors } =
    useContext(IntakeContext)

  // console.log(intakeState)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your user profile', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          Personal
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'firstName',
              label: 'First Name',
              placeholder: 'First Name',
              inputType: 'text',
              defaultErrorState: currentErrors[intakeState.firstName],
              value: intakeState.firstName,
              onChange: updateIntakeStateAndSubmit((firstName) =>
                updateIntakeStateAtKey({
                  firstName,
                  fullName: `${firstName} ${intakeState?.lastName}`,
                })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'lastName',
              label: 'Last Name',
              placeholder: 'Last Name',
              inputType: 'text',
              value: intakeState.lastName,
              onChange: updateIntakeStateAndSubmit((lastName) =>
                updateIntakeStateAtKey({
                  lastName,
                  fullName: `${intakeState?.firstName} ${lastName}`,
                })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'email',
              label: 'Email',
              placeholder: 'Email',
              inputType: 'text',
              value: intakeState.email,
              disabled: true,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'phone',
              label: 'phone number',
              placeholder: 'Eg. xxx-xxx-xxxx',
              inputType: 'tel',
              value: intakeState.phone,
              onChange: updateIntakeStateAndSubmit((phone) =>
                updateIntakeStateAtKey({ phone })
              ),
              defaultErrorState: currentErrors.phone,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'dateOfBirth',
              label: 'Date of Birth',
              placeholder: '01/01/1970',
              inputType: 'date',
              value: intakeState.dateOfBirth,
              onChange: updateIntakeStateAndSubmit((dateOfBirth) =>
                updateIntakeStateAtKey({ dateOfBirth })
              ),
            })}
          </Grid>
        </Grid>
        <Grid container columnSpacing={3} rowSpacing={2} mt={1}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'address',
              label: 'Street Address',
              placeholder: '12345 Main Street',
              inputType: 'text',
              value: intakeState.address,
              onChange: updateIntakeStateAndSubmit((address) =>
                updateIntakeStateAtKey({ address })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'address2',
              label: 'Apt, Suite, Bldg. (optional)',
              placeholder: 'Apt, Suite, Bldg. (optional)',
              inputType: 'text',
              value: intakeState.address2,
              onChange: updateIntakeStateAndSubmit((address2) =>
                updateIntakeStateAtKey({ address2 })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderMuiFormField({
              id: 'city',
              label: 'City',
              placeholder: 'City',
              inputType: 'text',
              value: intakeState.city,
              onChange: updateIntakeStateAndSubmit((city) =>
                updateIntakeStateAtKey({ city })
              ),
            })}
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type':
                {
                  display: 'flex',
                },
              '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type::before':
                {
                  content: '""',
                  width: '40px',
                  height: '24px',
                  marginRight: '8px',
                  backgroundImage: `url("${
                    selectData.states.find(
                      ({ value }) => value === intakeState?.state
                    )?.logo || ''
                  }")`,
                  backgroundSize: 'cover',
                },
            }}
          >
            {renderMuiFormField({
              autoFocus: false,
              id: 'state',
              label: 'State',
              placeholder: 'State',
              inputType: 'select',
              value: intakeState.state,
              onChange: updateIntakeStateAndSubmit((state) =>
                updateIntakeStateAtKey({ state })
              ),
              options: selectData.states,
            })}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderMuiFormField({
              id: 'zipcode',
              label: 'Zipcode',
              placeholder: '#####',
              inputType: 'zipcode',
              value: intakeState.zipcode,
              onChange: updateIntakeStateAndSubmit((zipcode) =>
                updateIntakeStateAtKey({ zipcode })
              ),
            })}
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default BasicInfo
